<script>
import Layout from '@layouts/main.vue';
import Checkout from '@views/checkout.vue';
import {
  authMethods,
  authComputed,
  modalMethods,
  mediaComputed,
  mediaMethods,
  trainerComputed,
  trainerMethods,
  organizationComputed,
} from '@state/helpers';
import appConfig from '@src/app.config';
import { required, email, minLength } from 'vuelidate/lib/validators';
import cloneDeep from 'lodash/cloneDeep';

const touchMap = new WeakMap();

export default {
  page: {
    title: 'Register',
    meta: [{ name: 'description', content: `Register for ${appConfig.title}` }],
  },
  components: {
    Layout,
    Checkout,
  },
  data() {
    return {
      trainerShape: {},
      firstName: '',
      lastName: '',
      email: '',
      country: '',
      password: '',
      planRequiredError: false,
      authError: null,
      tryingToRegister: false,
      passThroughQuery: {},
      planOpen: false,
      registerOpen: false,
      checkoutOpen: false,
      checkoutCompleted: false,
      photoPreview: null,
      imageToUpload: null,
      photoError: null,
      photoUploaderKey: 0,
      countries: [],
    };
  },
  validations: {
    email: {
      required,
      email,
    },
    country: {
      required,
    },
    firstName: {
      required,
    },
    lastName: {
      required,
    },
    password: {
      required,
      minLength: minLength(1),
    },
  },
  created: async function() {
    const trainerObject = await this.getTrainerShape({
      orgId: process.env.VUE_APP_ORG_ID,
    });
    this.trainerShape = trainerObject.trainer;

    if (this.$router.history.current.query) {
      this.passThroughQuery = this.$router.history.current.query;
      if (this.passThroughQuery.price && !this.currentUser) {
        this.planOpen = false;
        this.registerOpen = true;
        this.checkoutOpen = false;
      }

      if (
        this.passThroughQuery.price &&
        this.currentUser &&
        this.currentUser.products
      ) {
        this.checkoutOpen = true;
        // Set product because we are auth'd and we have a query param price
      }

      if (!this.passThroughQuery.price && !this.currentUser) {
        this.planOpen = true;
        // Set product because we are auth'd and we have a query param price
      }

      if (this.passThroughQuery.email) {
        this.email = this.passThroughQuery.email;
      }

      if (
        this.passThroughQuery.redirectFrom &&
        this.currentUser &&
        this.currentUser.roles.isTrainer &&
        this.currentUser.trainer.profile.subscribed
      ) {
        this.$router
          .push({ path: this.passThroughQuery.redirectFrom })
          .catch(() => {});
      }
    }
    this.getCountries();
  },
  computed: {
    ...authComputed,
    ...mediaComputed,
    ...trainerComputed,
    ...organizationComputed,
    activeProduct() {
      if (this.currentUser && this.currentUser.products) {
        return this.currentUser.products.find((product) => {
          return product.plans.find((plan) => {
            return plan.id === this.passThroughQuery.price;
          });
        });
      } else {
        return null;
      }
    },
    activePlan() {
      if (this.activeProduct) {
        return this.activeProduct.plans.find(
          (plan) => plan.id === this.passThroughQuery.price
        );
      } else {
        return {};
      }
    },
    profilePhoto() {
      return this.photoPreview
        ? this.photoPreview
        : require('@assets/images/placeholder-avatar.png');
    },
    currentCountry() {
      return (
        this.countries.length > 0 &&
        this.country &&
        this.countries.find((country) => country.alpha2Code === this.country)
      );
    },
  },
  methods: {
    ...authMethods,
    ...modalMethods,
    ...mediaMethods,
    ...trainerMethods,
    delayTouch($v) {
      $v.$reset();
      if (touchMap.has($v)) {
        clearTimeout(touchMap.get($v));
      }
      touchMap.set($v, setTimeout($v.$touch, 1000));
    },
    goToReferrerLink() {
      if (this.passThroughQuery.referrer) {
        window.location.href = this.passThroughQuery.referrer;
      } else {
        window.location.href = !this.passThroughQuery.price
          ? this.organizationPricingURL
          : '#';
      }
    },
    toggleArticle(index) {
      switch (index) {
        case 0:
          this.planOpen = !this.planOpen;
          this.registerOpen = false;
          this.checkoutOpen = false;
          this.goToReferrerLink();
          break;
        case 1:
          this.registerOpen = !this.registerOpen;
          this.planOpen = false;
          this.checkoutOpen = false;
          break;
        case 2:
          this.checkoutOpen = !this.checkoutOpen;
          this.planOpen = false;
          this.registerOpen = false;
          break;
        default:
          break;
      }
    },
    // Try to log the user in with the username
    // and password they provided.
    tryToRegister() {
      this.tryingToRegister = true;
      // Reset the authError if it existed.
      this.authError = null;
      this.$v.$touch();
      if (!this.$v.$invalid) {
        return this.register({
          trainer: {
            ...this.trainerShape,
            name: this.firstName + ' ' + this.lastName,
            firstName: this.firstName,
            lastName: this.lastName,
            email: this.email,
            password: this.password,
            contact_info: {
              country: this.country,
            },
          },
        })
          .then(async (response) => {
            if (
              this.currentUser &&
              response &&
              response.status &&
              response.status === 200 &&
              window.clevertap
            ) {
              const name = this.currentUser.roles.isTrainer
                ? this.currentUser.trainer.firstName
                : this.currentUser.roles.isAdmin
                ? this.currentUser.admin.admin
                : '';
              const email = this.currentUser.roles.isTrainer
                ? this.currentUser.trainer.email
                : this.currentUser.roles.isAdmin
                ? this.currentUser.isAdmin.user
                : '';
              const id = this.currentUser.roles.isTrainer
                ? this.currentUser.trainer._id
                : this.currentUser.roles.isAdmin
                ? this.currentUser.isAdmin._id
                : '';
              const userType = this.currentUser.roles.isTrainer
                ? 'Trainer'
                : this.currentUser.roles.isAdmin
                ? 'Admin'
                : '';

              if (name && email && id) {
                window.clevertap.profile.push({
                  Site: {
                    Name: name,
                    Email: email,
                    Identity: id,
                    app: 'Tandem',
                    'User-type': userType,
                  },
                });
                window.clevertap.event.push('Trainer Signup', {
                  trainer: email,
                  app: 'Tandem',
                });
              }

              if (
                this.currentUser.roles.isTrainer &&
                this.photoPreview &&
                this.imageToUpload
              ) {
                const registeredTrainer = cloneDeep(this.currentUser.trainer);
                this.handleS3Photo(registeredTrainer)
                  .then(() => {
                    if (this.hasNewMedia('cover', 'trainers')) {
                      registeredTrainer.mediaAssets = {
                        avatarImage: {
                          url: this.hasNewMedia('cover', 'trainers').publicUrl,
                        },
                      };
                      this.updateTrainer({
                        trainerObj: registeredTrainer,
                      }).then(async (res) => {
                        if (res && res.trainer) {
                          this.setSelectedTrainer(res.trainer);
                          this.clearPhoto();
                          this.validate();
                        }
                      });
                    }
                  })
                  .catch((e) => console.log(e));
              }

              this.tryingToRegister = false;
              this.registerOpen = false;
              this.checkoutOpen = true;
            }
            if (response && response.status && response.status !== 200) {
              if (response && response.data && response.data.errorMessage) {
                this.setNotifyModalContext({
                  textMain: 'Try again!',
                  textSecond: response.data.errorMessage,
                });
              } else {
                this.setNotifyModalContext({
                  textMain: 'Try again!',
                  textSecond: 'There was an error creating your account.',
                });
              }
              // this.setErrorText('There was an error creating your account.');
              this.$bvModal.show('notify-modal');
              this.tryingToRegister = false;
            }
          })
          .catch((error) => {
            this.tryingToregister = false;
            if (error && error.data && error.data.errorMessage) {
              this.setNotifyModalContext({
                textMain: 'Try again!',
                textSecond: error.data.errorMessage,
              });
            } else {
              this.setNotifyModalContext({
                textMain: 'Try again!',
                textSecond: 'There was an error creating your account.',
              });
            }
            this.$bvModal.show('notify-modal');
            this.authError = error.data.errorMessage;
          });
      } else {
        this.listenToError();
      }
      this.tryingToRegister = false;
    },
    toClients() {
      if (this.passThroughQuery.redirectFrom) {
        this.$router
          .push({ name: this.passThroughQuery.redirectFrom })
          .catch(() => {});
      } else {
        this.$router.push({ name: 'clients' }).catch(() => {});
      }
    },
    async onFileChange(evt) {
      const image = evt.target.files[0];
      const reader = new FileReader();
      if (image.size >= 10000000) {
        this.photoError =
          'Image is too large. Please keep images to less than 10 mbs.';
      } else {
        this.photoError = null;
        reader.readAsDataURL(image);
        reader.onload = (evt) => {
          this.photoPreview = evt.target.result;
        };
        this.imageToUpload = image;
      }
    },
    async handleS3Photo(trainer) {
      const trainerId = trainer._id;
      const orgId = trainer.org_id;
      const requestType = 'trainers';
      const type = 'cover';
      await this.getS3UploadUrl({
        file: this.imageToUpload,
        orgId,
        requestType,
        trainerId: trainerId || '0',
        type,
      });
      await this.uploadPhoto({ model: requestType, option: type });
    },
    clearPhoto() {
      this.photoError = null;
      this.imageToUpload = null;
      this.photoPreview = null;
      this.photoUploaderKey++;
      this.clearMedia();
    },
    listenToError() {
      if (this.$v.$anyError) {
        if (this.$v.firstName.$invalid) {
          this.authError = 'First Name is required';
        } else if (this.$v.lastName.$invalid) {
          this.authError = 'Last Name is required';
        } else if (this.$v.email.$invalid) {
          this.authError = 'Email is required';
        } else if (this.$v.password.$invalid) {
          this.authError = 'Password is required';
        } else if (this.$v.country.$invalid) {
          this.authError = 'Country is required';
        }
      } else {
        this.authError = null;
      }
    },
    getCountries() {
      fetch('https://restcountries.com/v2/all')
        .then((res) => res.json())
        .then((res) => {
          this.countries = res.map((country) => {
            return {
              label: country.name,
              ...country,
            };
          });
        });
    },
  },
  watch: {
    firstName() {
      this.listenToError();
    },
    lastName() {
      this.listenToError();
    },
    email() {
      this.listenToError();
    },
    password() {
      this.listenToError();
    },
    country() {
      this.listenToError();
    },
  },
};
</script>

<template>
  <Layout>
    <div class="container-wrapper clients register">
      <div class="content-wrapper" v-if="!checkoutCompleted">
        <h3 class="register-title"
          >If {{ organizationTitle }} helps you train even one more client each
          week, how much would that mean to you?</h3
        >
        <article class="card plan">
          <b-row @click="toggleArticle(0)">
            <b-col cols="2" md="1">
              <span class="icon-add-wrapper" ref="dayPlus">
                <img
                  class="plus"
                  v-if="!planOpen"
                  src="@assets/images/icon-plus.svg"
                />
                <img
                  class="minus"
                  v-if="planOpen"
                  src="@assets/images/icon-minus.svg"
                />
              </span>
            </b-col>
            <b-col class="text-left" cols="8">
              <p>1. Select your Plan</p>
            </b-col>
            <b-col cols="2" md="3">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" v-if="passThroughQuery.price" v-bind:svg-inline="''" :class="['check-complete']" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill="url(#pattern0)" d="M0 0h24v24H0z"/><defs><pattern id="pattern0" patternContentUnits="objectBoundingBox" width="1" height="1"><use xlink:href="#image0" transform="scale(.04167)"/></pattern><image id="image0" width="24" height="24" xlink:href="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAACb0lEQVRIDbVWv2sUURD+ZnZzd0mId/5CohBBEFFIoX+GdsJe4f8gBMSIgljY2IsYIlha3EKw0BQWgiAINlairSm0sDBBLombezMyL9njZfMu0Tuyzb438803s/PezCxhnyfrZLVes9HS9WIqSbghlKQGZ3U952STJmq/07XN1bydF4NoKKYwYjc5Nq3CLWWJYko7ElZiWU26Wz9ijvYYZ52sicnazBY0KUn+5T0GcugWK3k7Xwvxuxxknexkb7x++qCoQ4JwbV+Tbvz5nrfzn6Wcy4VFPgq58VhgxuGzsEPsHVjOLS3DRl4GWToxLs9pF8KEdqD/m/OQtLo2LuM0OfmrOF6/OEz0rHwG0AUovgrr7dDRznl8Sf0977ldhx0CB60TTU6pyjMA0wo0qjh/Hs1Gi62IqsqD9qnQcVVdNHIAXYDmYjbGzVahMeUgWSrcEmAR0BkAGwq6qSyfY3jj5rL8QwA5mmfh5+SSY6Fclaec5Rw4B0UBojll+RRiwrVx9+sgVBDjKqCXiWSBgSOmS5UnGPqEgAsK9IjolpB8DO1ia7bGVVUQ6C4Aa2Dn1dFTVpwQ1cekmIWSMOiOY3lftavujZutK1YVjuQDgeatRIhwSZVfAbgCkAC471jeVm1ie+Nma7lRJcs7APcsYoLWrRMAeCiJLMfwMZlxs/VzK4oYQFjfgOgBQOsAHgnLyxguJvOFZtymvP76xlkBjsaAw8oY+LV07cU3f4tsWPh+Pixbxc64jNPE3oGfRN1iZVCqKvb7bj3H9uDxY7RfBzaJbFiM4sTnfXvg9KfaniZ3qCOz/PZDHfqlE3v7WTHib8tfLaU+bgGoawUAAAAASUVORK5CYII="/></defs></svg>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" v-if="!passThroughQuery.price" v-bind:svg-inline="''" :class="['check-complete', { active: passThroughQuery.price }]" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm-1.25 17.292l-4.5-4.364 1.857-1.858 2.643 2.506 5.643-5.784 1.857 1.857-7.5 7.643z"/></svg>
            </b-col>
          </b-row>
          <b-row :class="{ content: true, open: planOpen }">
            <b-col>
              <div>
                <h2>{{
                  passThroughQuery.price
                    ? 'Product Pre Selected'
                    : 'No Product Selected'
                }}</h2>
              </div>
            </b-col>
          </b-row>
        </article>
        <article class="card form">
          <b-row @click="toggleArticle(1)">
            <b-col cols="2" md="1">
              <span class="icon-add-wrapper" ref="dayPlus">
                <img
                  class="plus"
                  v-if="!registerOpen"
                  src="@assets/images/icon-plus.svg"
                />
                <img
                  class="minus"
                  v-if="registerOpen"
                  src="@assets/images/icon-minus.svg"
                />
              </span>
            </b-col>
            <b-col class="text-left" cols="8">
              <p>2. Create your Account</p>
            </b-col>
            <b-col cols="2" md="3">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" v-if="currentUser" v-bind:svg-inline="''" :class="['check-complete']" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill="url(#pattern0)" d="M0 0h24v24H0z"/><defs><pattern id="pattern0" patternContentUnits="objectBoundingBox" width="1" height="1"><use xlink:href="#image0" transform="scale(.04167)"/></pattern><image id="image0" width="24" height="24" xlink:href="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAACb0lEQVRIDbVWv2sUURD+ZnZzd0mId/5CohBBEFFIoX+GdsJe4f8gBMSIgljY2IsYIlha3EKw0BQWgiAINlairSm0sDBBLombezMyL9njZfMu0Tuyzb438803s/PezCxhnyfrZLVes9HS9WIqSbghlKQGZ3U952STJmq/07XN1bydF4NoKKYwYjc5Nq3CLWWJYko7ElZiWU26Wz9ijvYYZ52sicnazBY0KUn+5T0GcugWK3k7Xwvxuxxknexkb7x++qCoQ4JwbV+Tbvz5nrfzn6Wcy4VFPgq58VhgxuGzsEPsHVjOLS3DRl4GWToxLs9pF8KEdqD/m/OQtLo2LuM0OfmrOF6/OEz0rHwG0AUovgrr7dDRznl8Sf0977ldhx0CB60TTU6pyjMA0wo0qjh/Hs1Gi62IqsqD9qnQcVVdNHIAXYDmYjbGzVahMeUgWSrcEmAR0BkAGwq6qSyfY3jj5rL8QwA5mmfh5+SSY6Fclaec5Rw4B0UBojll+RRiwrVx9+sgVBDjKqCXiWSBgSOmS5UnGPqEgAsK9IjolpB8DO1ia7bGVVUQ6C4Aa2Dn1dFTVpwQ1cekmIWSMOiOY3lftavujZutK1YVjuQDgeatRIhwSZVfAbgCkAC471jeVm1ie+Nma7lRJcs7APcsYoLWrRMAeCiJLMfwMZlxs/VzK4oYQFjfgOgBQOsAHgnLyxguJvOFZtymvP76xlkBjsaAw8oY+LV07cU3f4tsWPh+Pixbxc64jNPE3oGfRN1iZVCqKvb7bj3H9uDxY7RfBzaJbFiM4sTnfXvg9KfaniZ3qCOz/PZDHfqlE3v7WTHib8tfLaU+bgGoawUAAAAASUVORK5CYII="/></defs></svg>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" v-if="!currentUser" v-bind:svg-inline="''" :class="['check-complete', { active: currentUser }]" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm-1.25 17.292l-4.5-4.364 1.857-1.858 2.643 2.506 5.643-5.784 1.857 1.857-7.5 7.643z"/></svg>
            </b-col>
          </b-row>
          <b-row :class="{ content: true, open: registerOpen }">
            <b-col v-if="!currentUser">
              <form class="login-form" @submit.prevent="tryToRegister">
                <div class="form-header">
                  <h1>Create Account</h1>
                </div>
                <div class="row profile-pic-wrapper">
                  <div class="col">
                    <label>Profile Picture</label>
                    <div class="photo-uploader">
                      <div class="helper-text margin-bottom">
                        Photos must be .jpg, .jpeg, .gif, or .webp, no larger
                        than 10MB.
                      </div>
                      <input
                        :key="photoUploaderKey"
                        type="file"
                        class="input-photo btn-secondary"
                        name="file"
                        accept="image/png,image/jpg,image/jpeg,image/gif,image/webp"
                        @change="onFileChange"
                      />
                      <p class="helper-text text-danger" v-show="photoError">{{
                        photoError
                      }}</p>
                    </div>
                    <span v-if="photoPreview" @click="clearPhoto"
                      >clear photo</span
                    >
                  </div>
                  <div class="col-3 avatar-wrapper">
                    <BaseThumbnail :image="profilePhoto" :round="true" />
                  </div>
                </div>
                <div class="row full-name-wrapper">
                  <div class="col">
                    <label for="firstName">First Name</label>
                    <BaseInputText
                      :class="[{ 'input-error': $v.firstName.$error }]"
                      v-model="firstName"
                      name="firstName"
                      placeholder="First Name"
                    />
                  </div>
                  <div class="col">
                    <label for="lastName">Last name</label>
                    <BaseInputText
                      :class="[{ 'input-error': $v.lastName.$error }]"
                      v-model="lastName"
                      name="lastName"
                      placeholder="Last Name"
                    />
                  </div>
                </div>

                <label for="email">Email</label>
                <BaseInputText
                  :class="[{ 'input-error': $v.email.$error }]"
                  v-model="email"
                  name="email"
                  type="email"
                  placeholder="email@gmail.com"
                />

                <label for="password">Password</label>
                <BaseInputText
                  :class="[{ 'input-error': $v.password.$error }]"
                  v-model="password"
                  name="password"
                  type="password"
                  placeholder="••••••••••"
                />

                <label for="country">Country</label>
                <div class="input-country-wrapper">
                  <v-select
                    name="country"
                    placeholder="Choose country"
                    :options="countries"
                    :searchable="true"
                    :clearable="false"
                    @input="country = $event.alpha2Code"
                    :value="currentCountry"
                    :class="[{ 'input-error': $v.country.$error }]"
                  />
                </div>

                <div class="login-footer">
                  <p class="terms"
                    >By creating an account, you agree to the
                    <a href="/privacy">Privacy Policy</a> and
                    <a href="/terms">Terms of Use</a>.</p
                  >
                  <BaseButton
                    :key="tryingToRegister"
                    :disabled="tryingToRegister"
                    class="btn-primary btn-round"
                    type="submit"
                  >
                    <BaseIcon v-if="tryingToRegister" name="sync" spin />
                    <span v-else>
                      Create Account
                    </span>
                  </BaseButton>
                </div>
                <p class="error" v-if="authError">
                  {{ authError }}
                </p>
              </form>
            </b-col>
            <b-col v-else>
              <div class="loading-wrapper" v-if="tryingToRegister">
                <BaseLoadingSpinner />
              </div>
              <p v-if="!tryingToRegister"
                >Welcome back {{ currentUser.trainer.email }}</p
              >
            </b-col>
          </b-row>
        </article>
        <article class="card">
          <b-row @click="toggleArticle(2)">
            <b-col cols="2" md="1">
              <transition name="fade">
                <span class="icon-add-wrapper" ref="dayPlus">
                  <img
                    class="plus"
                    v-if="!checkoutOpen"
                    src="@assets/images/icon-plus.svg"
                  />
                  <img
                    class="minus"
                    v-if="checkoutOpen"
                    src="@assets/images/icon-minus.svg"
                  /> </span
              ></transition>
            </b-col>
            <b-col class="text-left" cols="8">
              <p>3. Checkout</p>
            </b-col>
            <b-col cols="2" md="3">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" v-if="false" v-bind:svg-inline="''" :class="['check-complete']" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill="url(#pattern0)" d="M0 0h24v24H0z"/><defs><pattern id="pattern0" patternContentUnits="objectBoundingBox" width="1" height="1"><use xlink:href="#image0" transform="scale(.04167)"/></pattern><image id="image0" width="24" height="24" xlink:href="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAACb0lEQVRIDbVWv2sUURD+ZnZzd0mId/5CohBBEFFIoX+GdsJe4f8gBMSIgljY2IsYIlha3EKw0BQWgiAINlairSm0sDBBLombezMyL9njZfMu0Tuyzb438803s/PezCxhnyfrZLVes9HS9WIqSbghlKQGZ3U952STJmq/07XN1bydF4NoKKYwYjc5Nq3CLWWJYko7ElZiWU26Wz9ijvYYZ52sicnazBY0KUn+5T0GcugWK3k7Xwvxuxxknexkb7x++qCoQ4JwbV+Tbvz5nrfzn6Wcy4VFPgq58VhgxuGzsEPsHVjOLS3DRl4GWToxLs9pF8KEdqD/m/OQtLo2LuM0OfmrOF6/OEz0rHwG0AUovgrr7dDRznl8Sf0977ldhx0CB60TTU6pyjMA0wo0qjh/Hs1Gi62IqsqD9qnQcVVdNHIAXYDmYjbGzVahMeUgWSrcEmAR0BkAGwq6qSyfY3jj5rL8QwA5mmfh5+SSY6Fclaec5Rw4B0UBojll+RRiwrVx9+sgVBDjKqCXiWSBgSOmS5UnGPqEgAsK9IjolpB8DO1ia7bGVVUQ6C4Aa2Dn1dFTVpwQ1cekmIWSMOiOY3lftavujZutK1YVjuQDgeatRIhwSZVfAbgCkAC471jeVm1ie+Nma7lRJcs7APcsYoLWrRMAeCiJLMfwMZlxs/VzK4oYQFjfgOgBQOsAHgnLyxguJvOFZtymvP76xlkBjsaAw8oY+LV07cU3f4tsWPh+Pixbxc64jNPE3oGfRN1iZVCqKvb7bj3H9uDxY7RfBzaJbFiM4sTnfXvg9KfaniZ3qCOz/PZDHfqlE3v7WTHib8tfLaU+bgGoawUAAAAASUVORK5CYII="/></defs></svg>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" v-if="true" v-bind:svg-inline="''" :class="['check-complete', { active: false }]" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm-1.25 17.292l-4.5-4.364 1.857-1.858 2.643 2.506 5.643-5.784 1.857 1.857-7.5 7.643z"/></svg>
            </b-col>
          </b-row>
          <b-row :class="{ content: true, open: checkoutOpen }">
            <b-col class="checkout-col">
              <Checkout
                :passThroughQuery="passThroughQuery"
                @set-checkout-completed="checkoutCompleted = $event"
                :plan="activePlan"
              />
            </b-col>
          </b-row>
        </article>
      </div>
      <div class="content-wrapper success-container" v-if="checkoutCompleted">
        <b-row class="success">
          <div class="success-wrapper">
            <div>
              <div class="success-check">
                <svg width="66" height="66" viewBox="0 0 66 66" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-check'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M33 66c18.225 0 33-14.775 33-33S51.225 0 33 0 0 14.775 0 33s14.775 33 33 33z" fill="url(#paint0_linear)" fill-opacity=".6"/><path d="M17.368 33.55l4.466-5.321 8.645 7.255 15.748-18.116 5.243 4.56L35.803 39.95l-4.466 5.322L17.368 33.55z" fill="#fff"/><defs><linearGradient id="paint0_linear" x1="33" y1="0" x2="33" y2="66" gradientUnits="userSpaceOnUse"><stop stop-color="#55BCF5"/><stop offset="1" stop-color="#3E4FE2"/></linearGradient></defs></svg>
              </div>
              <h2>You're all set!</h2>
              <p
                >Welcome to the {{ organizationTitle }} Team! Click the continue
                button to get started.</p
              ></div
            >
            <BaseButton
              @click="toClients"
              class="button btn-secondary large"
              type="button"
            >
              <div>Continue</div>
            </BaseButton>
          </div>
        </b-row>
      </div>
    </div>
  </Layout>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable selector-pseudo-element-no-unknown

::v-deep .input-country-wrapper {
  position: relative;

  .v-select {
    &.input-error {
      .vs__dropdown-toggle {
        background-color: rgba($color-danger-wo-rgb, 0.08);
        border: 1px solid $color-danger;
      }
    }
  }

  svg {
    margin-top: -2px;
    position: absolute;
    right: 20px;
    top: 50%;
  }
}

.loading-wrapper {
  display: flex;
  justify-content: center;
  width: 100%;
  ::v-deep .loading {
    max-height: 2.5rem;
    max-width: 2.5rem;
  }
}

.container-wrapper.register {
  background-color: $color-off-white-one;

  .content-wrapper {
    padding: 3.2rem 1.6rem;

    // Tablet up
    @include media('xl', null) {
      padding: 3.2rem;
    }
  }
}

.planSelect {
  input {
    display: none;
  }
}

.register-title {
  font-size: 34.101px;
  font-style: normal;
  font-weight: 600;
  letter-spacing: -0.165769px;
  line-height: 41px;
  margin: auto;
  padding-bottom: 3rem;
  padding-top: 3rem;
  text-align: center;

  // Tablet up
  @include media('xl', null) {
    width: 70%;
  }

  // XXL Tablet up
  @include media('xxl', null) {
    width: 50%;
  }
}

.card {
  background-color: white;
  margin: 0 auto;
  padding: 2rem;
  text-align: center;

  // Tablet up
  @include media('xl', null) {
    min-width: 500px;
    width: 50%;
  }

  &:nth-child(2) {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    box-shadow: 0 2px 18px rgba($color-gray-wo-rgb, 0.25);
  }

  &:last-child {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    box-shadow: 0 2px 18px rgba($color-gray-wo-rgb, 0.25);
  }

  .row {
    &:not(.content) {
      transition: transform 200ms;
      transition-timing-function: ease-out;

      &:hover {
        cursor: pointer;
        transform: scale(1.01);
      }
    }
  }

  .checkout-col {
    padding-left: unset;
    padding-right: unset;
  }
}

.content {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
  transition: 0.3s ease all;
}

.content.open {
  border-top: 2px solid rgba($color-gray-wo-rgb, 0.3);
  margin-top: 2.5%;
  max-height: 108rem;
  opacity: 1;
  overflow: hidden;
  padding: 4.5% 0 5%;
  transition: opacity 0.8s ease;

  &:last-child {
    // Tablet up
    @include media('xl', null) {
      max-height: 105rem;
    }
  }

  // Tablet up
  @include media('xl', null) {
    padding: 7%;
  }
}

.text-left {
  align-items: center;
  display: flex;
  justify-content: flex-start;

  p {
    margin-bottom: unset;
  }
}

.success-container {
  .success {
    align-items: center;
    display: flex;
    justify-content: center;
    margin-top: 6%;

    .success-wrapper {
      align-items: center;
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding: 0 3rem;

      // Tablet up
      @include media('xl', null) {
        flex: 0 0 53.333333%;
        max-width: 53.333333%;
        padding: 0;
      }

      div {
        align-items: center;
        display: flex;
        flex-direction: column;
        justify-content: center;

        // Tablet up
        @include media('xl', null) {
          flex: 0 0 53.333333%;
          max-width: 53.333333%;
        }
      }

      h2 {
        color: black;
        font-family: $base-font-family;
        font-size: 35px;
        font-style: normal;
        font-weight: bold;
        letter-spacing: -0.761905px;
        line-height: 52px;
        text-align: center;
        white-space: nowrap;
      }

      p {
        color: black;
        font-size: 16px;
        font-style: normal;
        font-weight: normal;
        line-height: 23px;
        text-align: center;
      }

      .success-check {
        align-items: center;
        background: white;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        margin-bottom: 3rem;
        position: relative;
        width: fit-content;
        z-index: 9999;

        svg {
          background: linear-gradient(
            180deg,
            $color-primary-light-one 0%,
            $color-primary-six 100%
          );
          border-radius: 50%;
          height: 10rem;
          width: 10rem;
        }
      }
    }

    .btn-secondary {
      align-items: center;
      display: flex;
      justify-content: center;
      margin-top: 4rem;
      max-width: 35rem;
      width: 75%;

      // LG Mobile up
      @include media('lg', null) {
        width: 60%;
      }

      // XXL Tablet up
      @include media('xxl', null) {
        width: 35%;
      }
    }
  }
}

.login-form {
  label {
    display: block;
    margin: 0;
    text-align: left;
  }

  .error {
    color: red;
    font-size: 14px;
  }

  &.plan-select {
    label {
      display: block;
      margin: 0;
      text-align: left;

      &.product-item {
        align-items: center;
        border: 1px solid rgba($color-light-gray-twelve-wo-rgb, 0.8);
        border-radius: 8px;
        box-shadow: 0 1px 4px rgba($color-black-wo-rgb, 0.1);
        cursor: pointer;
        display: flex;
        justify-items: space-between;
        margin-bottom: 10px;
        padding: 24px;

        &:hover {
          background-color: $color-off-white-three;
        }

        h3,
        p,
        h2 {
          margin-bottom: 0;
        }

        h3 {
          font-size: 1.8rem;
          font-weight: 600;
        }

        .left-lock {
          margin: auto;
          margin-left: 0;
        }

        .right-lock {
          margin: auto;
          margin-right: 0;
        }
      }
    }

    input[type='radio']:checked + label {
      background-color: rgba($color-action-wo-rgb, 0.5);
    }
  }
}

.login-footer {
  display: block;

  // Tablet up
  @include media('xl', null) {
    align-items: flex-start;
    display: flex;
  }

  .terms {
    flex-basis: 50%;
    flex-grow: 2;
    font-size: 1.4rem;
    font-weight: normal;
    line-height: 2rem;
    margin-bottom: 2rem;
    text-align: center;

    // Tablet up
    @include media('xl', null) {
      margin-bottom: 1rem;
      text-align: left;
    }

    a {
      color: $color-primary-two;
      font-weight: normal;
      letter-spacing: unset;
      text-decoration: none;
    }
  }

  button {
    background-color: $color-action !important;
    border: 2px solid $color-action !important;
    border-radius: 2.5rem;
    color: white;
    &:hover {
      background-color: $color-primary-eleven !important;
    }
    // Tablet up
    @include media('xl', null) {
      margin-left: 35px;
    }
  }
}

.form-header {
  pre {
    color: $color-action;
    font-family: $base-font-family;
    font-size: 1.7rem;
    font-weight: 800;
    letter-spacing: 0.1px;
    line-height: 2.2rem;
    margin: 0;
    text-align: left;
  }

  h1 {
    font-family: $base-font-family;
    font-size: 2.8rem;
    font-weight: black;
    letter-spacing: -0.76px;
    line-height: 5.2rem;
    margin: 0;
    margin-bottom: 24px;
    text-align: left;

    // Tablet up
    @include media('xl', null) {
      font-size: 4.8rem;
    }
  }
}

::v-deep .payment-form .payment-button {
  // LG Mobile up
  @include media('lg', null) {
    width: 60%;
  }

  // XXL Tablet up
  @include media('xxl', null) {
    width: 35%;
  }
}

.check-complete {
  outline: none;

  &:not(.active) {
    fill: rgb(214, 214, 214);
  }
}

.icon-add-wrapper {
  align-items: center;
  background-color: white;
  border: 2px solid $color-action;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  height: 3rem;
  justify-content: center;
  padding: 0.4rem;
  transition: transform 200ms;
  transition-timing-function: ease-out;
  width: 3rem;

  img {
    border-radius: 50%;
    height: 100%;
    width: 100%;

    &.minus {
      font-weight: 900;
      margin-left: 0.1rem;
      width: 80%;
    }

    &.plus {
      font-weight: 900;
      height: 80%;
      margin-left: 0.1rem;
      width: 90%;
    }
  }
}

.offset-4 {
  align-items: center;
  display: flex;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.profile-pic-wrapper {
  transition: transform unset !important;
  transition-timing-function: unset !important;
  width: 100%;
  &:hover {
    cursor: unset !important;
    transform: unset !important;
  }
  span {
    bottom: 48px;
    color: $color-medium-gray-alt;
    cursor: pointer;
    font-size: 1.5rem;
    font-weight: 800;
    left: 200px;
    position: absolute;
    white-space: nowrap;
    &:hover {
      color: $color-medium-gray-seven;
    }
  }
}
.photo-uploader {
  .input-photo {
    background-color: transparent;
    box-shadow: none;
    width: 19rem;
    @extend %input-file-upload;

    &::before {
      @extend %input-file-upload-btn;

      border: 2px solid $color-action;
      color: $color-action;
      content: 'Upload Profile Photo';
    }

    &:hover {
      &::before {
        color: $color-off-white;
      }
    }
  }

  align-items: flex-start;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  margin-bottom: 3rem;
  margin-top: 0.5rem;
  text-align: start;
}

.full-name-wrapper {
  transition: transform unset !important;
  transition-timing-function: unset !important;
  &:hover {
    cursor: unset !important;
    transform: unset !important;
  }
}

.avatar-wrapper {
  ::v-deep img {
    height: 6.5rem;
    width: 6.5rem;
  }
}
</style>
